import { Box, Center, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router';
import { SectionAddAction } from 'src/cashflows/components/SectionAddButton';
import { formatMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { getCategoryEntries } from 'src/financial/financial-data/rework/addProductEntries';
import { FinancialSectionTable } from 'src/financial/financial-data/rework/FinancialSectionTable';
import { FinancialRow, netWorthOf } from 'src/financial/financial-data/rework/financialRows';
import { useCreateFinancialItem } from 'src/financial/financial-data/financial-item/useCreateFinancialItem';
import { type OwnerSelection } from 'src/financial/financial-data/financial-item/FamilyOwnerModal';
import { financialItemPath } from 'src/financial/financial-data/financial-item/financialItemPaths';
import { DataLoadError } from 'src/common/components/data-states/DataLoadError';
import { NoRecordedDataCallout } from 'src/common/components/data-states/NoRecordedDataCallout';
import { FAMILY_FINANCIAL_SIESTA_PROMPT } from 'src/financial/financial-data/rework/emptyFinancialState';
import { FamilyMember } from './useFamilyMembers';
import { useFamilyFinancialItems } from './useFamilyFinancialItems';

export function FamilyFinancialPanel({ members }: { members: FamilyMember[] }) {
  const { categories, isLoading, hasErrors, allFailed, refetch } = useFamilyFinancialItems(members);
  const { flagEnabledFinancialItemOwners } = useFlags();
  const navigate = useNavigate();
  const currency = useCurrency();

  // An item opens through a member whose list returned it, not whoever is browsing the family —
  // but it was opened from the family, so that is where saving it returns to.
  const familyId = members.find(member => member.client.familyId)?.client.familyId;
  const returnTo = familyId ? `/family/${familyId}/financial` : undefined;

  const handleRowClick = (row: FinancialRow) =>
    navigate(financialItemPath(row.openAsClientId, row.navProduct, row.id, returnTo));

  // An item added from the family view usually belongs to the whole family, so the owner picker
  // opens with every member ticked and the adviser confirms or trims it. The record still has to
  // hang off one client: the family's first member if they own it, otherwise the first owner
  // picked.
  const anchorMember = members[0];
  const familyOwners: OwnerSelection[] = members.map(member => ({ client_id: member.clientId }));

  const { beginCreate, modals } = useCreateFinancialItem({
    clientId: anchorMember?.clientId ?? '',
    members,
    returnTo,
    // The first member is only where this view is anchored, so an item that cannot record owners
    // asks which member it is for rather than filing itself against them.
    chooseHolder: true,
    lockClientAsOwner: false,
  });

  const addActionForCategory = (key: string): SectionAddAction | undefined => {
    // Without the owners feature there is nobody to attribute a family-level item to, so the
    // section offers no Add control rather than a button that cannot do anything.
    if (!flagEnabledFinancialItemOwners || anchorMember == null) {
      return undefined;
    }
    switch (key) {
      case 'spendingAccounts':
        return { onAdd: () => beginCreate('SPENDING_ACCOUNT', 'SpendingAccount', familyOwners) };
      case 'protections':
        return { onAdd: () => beginCreate('PROTECTION', 'Protection', familyOwners) };
      case 'assets':
        return {
          entries: getCategoryEntries('ASSET'),
          onSelect: product => beginCreate('ASSET', product, familyOwners),
        };
      case 'liabilities':
        return {
          entries: getCategoryEntries('LIABILITY'),
          onSelect: product => beginCreate('LIABILITY', product, familyOwners),
        };
      default:
        return undefined;
    }
  };

  const renderSection = (category: (typeof categories)[number]) => (
    <FinancialSectionTable
      key={category.key}
      title={category.title}
      rows={category.rows}
      total={category.total}
      fullTotal={category.fullTotal}
      totalLabel={category.totalLabel}
      subSections={category.subSections}
      onRowClick={handleRowClick}
      showOwnership
      ownershipLabel="Family owners"
      addAction={addActionForCategory(category.key)}
      hideTotal={category.key === 'protections'}
    />
  );

  // Laid out as a client's own Financials is: what the household holds and owes, then what it
  // comes to, then protection below the line it is left out of.
  const balanceSheetCategories = categories.filter(category => category.key !== 'protections');
  const protectionCategory = categories.find(category => category.key === 'protections');

  const hasAnyItems = categories.some(category => category.rows.length > 0);

  // Nothing arrived at all, so there are no totals to qualify — showing the sections now would
  // put £0 against each one, which reads as a household that owns nothing.
  if (!isLoading && allFailed) {
    return (
      <Stack gap="lg">
        <DataLoadError
          title="Financial data could not be loaded"
          subject="the household's"
          what="financial data"
          onRetry={refetch}
          testId="family-financial-load-error"
        />
        {modals}
      </Stack>
    );
  }

  return (
    <Box>
      <Stack gap="lg">
        {hasErrors && (
          <DataLoadError
            title="Some data could not be loaded"
            onRetry={refetch}
            testId="family-financial-partial-error"
          >
            Financial data for one or more family members failed to load, so the totals below may be
            incomplete.
          </DataLoadError>
        )}
        {!isLoading && !hasErrors && !hasAnyItems && members.length > 0 && (
          <NoRecordedDataCallout
            title="No financial data recorded yet"
            subjects={members.map(member => ({
              clientId: member.clientId,
              firstName: member.client.firstName,
            }))}
            siestaPrompt={FAMILY_FINANCIAL_SIESTA_PROMPT}
            testId="family-financial-empty-state"
          >
            Nothing is recorded for this household yet. Process their documents or ask Siesta to
            pull in the accounts, pensions, investments, property, liabilities and policies
            {/* Without the owners feature no section has an Add control, so offering the adviser
                one would point at nothing. */}
            {flagEnabledFinancialItemOwners && ' — or add an item by hand from any section below'}.
          </NoRecordedDataCallout>
        )}
        {isLoading ? (
          <Paper variant="content">
            <Center py="xxl">
              <Loader />
            </Center>
          </Paper>
        ) : (
          balanceSheetCategories.map(renderSection)
        )}
      </Stack>

      {/* A figure stood against part-loaded data would read as the household's worth, so it waits
          until every member's items are in. The warning above already covers a member that failed.
          With nothing recorded, £0 would read as a household that owns nothing. */}
      {!isLoading && hasAnyItems && (
        <Group justify="flex-end" gap={8} mt={24}>
          <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.5">
            Net worth:
          </Text>
          <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {formatMoney(netWorthOf(categories), currency)}
          </Text>
        </Group>
      )}

      {!isLoading && protectionCategory && <Box mt={24}>{renderSection(protectionCategory)}</Box>}

      {modals}
    </Box>
  );
}
